import React, { useCallback, useEffect, useId, useState } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { reportProfile } from '../../util/api';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ReviewRating,
  Button,
  SecondaryButton,
  InlineTextButton,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  Modal,
  CustomExtendedDataSection,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import {
  LISTING_GROUP_ACTIVE,
  LISTING_GROUP_ARCHIVED,
  LISTING_GROUP_AVAILABLE,
  LISTING_STATUS_DRAFT,
  LISTING_STATUS_PENDING_APPROVAL,
} from './ProfilePage.listingClassification';
import { archiveOwnDraft } from './ProfilePage.duck';
import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
const LISTING_FILTER_ALL = 'all';

const OWN_CLIENT_LISTING_GROUPS = [
  {
    group: LISTING_GROUP_AVAILABLE,
    headingId: 'ProfilePage.ownListingsAvailable',
    emptyId: 'ProfilePage.ownListingsAvailableEmpty',
    filterId: 'ProfilePage.ownListingsFilterAvailable',
  },
  {
    group: LISTING_GROUP_ACTIVE,
    headingId: 'ProfilePage.ownListingsActive',
    emptyId: 'ProfilePage.ownListingsActiveEmpty',
    filterId: 'ProfilePage.ownListingsFilterActive',
  },
  {
    group: LISTING_GROUP_ARCHIVED,
    headingId: 'ProfilePage.ownListingsArchived',
    emptyId: 'ProfilePage.ownListingsArchivedEmpty',
    filterId: 'ProfilePage.ownListingsFilterArchived',
  },
];

const listingCreatedAtDate = listing => {
  const createdAt = listing?.attributes?.createdAt;
  if (!createdAt) {
    return null;
  }
  const date = new Date(createdAt);
  return Number.isFinite(date.getTime()) ? date : null;
};

export const OwnClientListings = props => {
  const {
    listings,
    classifications,
    intl,
    ownerId,
    archiveDraftInProgressId,
    archiveDraftError,
    onArchiveDraft,
    onManageDisableScrolling = () => null,
  } = props;
  const [listingFilter, setListingFilter] = useState(LISTING_FILTER_ALL);
  const [draftToArchive, setDraftToArchive] = useState(null);
  const visibleGroups =
    listingFilter === LISTING_FILTER_ALL
      ? OWN_CLIENT_LISTING_GROUPS
      : OWN_CLIENT_LISTING_GROUPS.filter(({ group }) => group === listingFilter);
  const archiveInProgress = archiveDraftInProgressId === draftToArchive?.id?.uuid;

  const closeArchiveModal = () => {
    if (!archiveInProgress) {
      setDraftToArchive(null);
    }
  };

  const confirmArchiveDraft = async () => {
    if (!draftToArchive || archiveInProgress) {
      return;
    }

    try {
      await onArchiveDraft(draftToArchive.id, ownerId);
      setDraftToArchive(null);
    } catch (error) {
      // The Redux state renders a safe localized error and keeps the draft in its prior group.
    }
  };

  return (
    <section className={css.ownListingsContainer}>
      <H4 as="h2" className={css.ownListingsTitle}>
        <FormattedMessage id="ProfilePage.ownListingsTitle" />
      </H4>
      <div className={css.ownListingsControls}>
        <label className={css.ownListingsFilterLabel} htmlFor="own-listings-filter">
          <FormattedMessage id="ProfilePage.ownListingsFilterLabel" />
        </label>
        <select
          id="own-listings-filter"
          className={css.ownListingsFilterSelect}
          value={listingFilter}
          onChange={event => setListingFilter(event.target.value)}
        >
          <option value={LISTING_FILTER_ALL}>
            {intl.formatMessage({ id: 'ProfilePage.ownListingsFilterAll' })}
          </option>
          {OWN_CLIENT_LISTING_GROUPS.map(({ group, filterId }) => (
            <option key={group} value={group}>
              {intl.formatMessage({ id: filterId })}
            </option>
          ))}
        </select>
      </div>
      {visibleGroups.map(({ group, headingId, emptyId }) => {
        const groupListings = listings.filter(
          listing => classifications[listing.id.uuid]?.group === group
        );

        return (
          <div key={group} className={css.ownListingsGroup}>
            <h3 className={css.ownListingsGroupTitle}>
              <FormattedMessage id={headingId} values={{ count: groupListings.length }} />
            </h3>
            {groupListings.length > 0 ? (
              <ul className={css.ownListingsGrid}>
                {groupListings.map(listing => {
                  const { state, title } = listing.attributes || {};
                  const createdAtDate = listingCreatedAtDate(listing);
                  const classification = classifications[listing.id.uuid];
                  const isArchived = classification?.group === LISTING_GROUP_ARCHIVED;
                  const canArchiveDraft =
                    ['draft', 'pendingApproval'].includes(listing.attributes?.state) &&
                    listing.attributes?.privateData?.archivedByAuthor !== true &&
                    [LISTING_STATUS_DRAFT, LISTING_STATUS_PENDING_APPROVAL].includes(
                      classification?.status
                    );
                  const linkName =
                    state === 'draft' || state === 'pendingApproval'
                      ? 'ListingPageVariant'
                      : 'ListingPage';
                  const linkParams =
                    linkName === 'ListingPageVariant'
                      ? {
                          id: listing.id.uuid,
                          slug: createSlug(title),
                          variant:
                            state === 'draft'
                              ? LISTING_PAGE_DRAFT_VARIANT
                              : LISTING_PAGE_PENDING_APPROVAL_VARIANT,
                        }
                      : undefined;

                  return (
                    <li key={listing.id.uuid} className={css.ownListingCard}>
                      <ListingCard
                        listing={listing}
                        showAuthorInfo={false}
                        linkName={linkName}
                        linkParams={linkParams}
                        disableLink={isArchived}
                      />
                      <div className={css.ownListingMeta}>
                        <span className={css.ownListingStatus}>
                          <FormattedMessage id={`ProfilePage.status.${classification.status}`} />
                        </span>
                        {createdAtDate != null ? (
                          <span className={css.ownListingDate}>
                            {intl.formatDate(createdAtDate, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        ) : null}
                      </div>
                      {canArchiveDraft ? (
                        <InlineTextButton
                          className={css.archiveDraftButton}
                          disabled={!!archiveDraftInProgressId}
                          onClick={() => setDraftToArchive(listing)}
                        >
                          <FormattedMessage id="ProfilePage.archiveDraft" />
                        </InlineTextButton>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={css.ownListingsEmpty}>
                <FormattedMessage id={emptyId} />
              </p>
            )}
          </div>
        );
      })}
      <Modal
        id="ProfilePage.archiveDraftModal"
        containerClassName={css.archiveDraftModal}
        contentClassName={css.archiveDraftModalContent}
        isOpen={!!draftToArchive}
        onClose={closeArchiveModal}
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={intl.formatMessage({ id: 'ProfilePage.archiveDraftCancel' })}
        usePortal
      >
        <h3 className={css.archiveDraftModalTitle}>
          <FormattedMessage id="ProfilePage.archiveDraftTitle" />
        </h3>
        <p className={css.archiveDraftModalMessage}>
          <FormattedMessage id="ProfilePage.archiveDraftMessage" />
        </p>
        {archiveDraftError ? (
          <p className={css.archiveDraftError} role="alert">
            <FormattedMessage id="ProfilePage.archiveDraftError" />
          </p>
        ) : null}
        <div className={css.archiveDraftModalActions}>
          <SecondaryButton
            type="button"
            className={css.archiveDraftCancelButton}
            onClick={closeArchiveModal}
            disabled={archiveInProgress}
          >
            <FormattedMessage id="ProfilePage.archiveDraftCancel" />
          </SecondaryButton>
          <Button
            type="button"
            className={css.archiveDraftConfirmButton}
            onClick={confirmArchiveDraft}
            inProgress={archiveInProgress}
            disabled={archiveInProgress}
          >
            <FormattedMessage id="ProfilePage.archiveDraftConfirm" />
          </Button>
        </div>
      </Modal>
    </section>
  );
};

export const FounderBadge = props => {
  const { providerBadge, className } = props;
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipId = useId();

  return providerBadge === 'founder' ? (
    <div
      className={classNames(css.founderBadge, css.founderBadgeWithTooltip, className, {
        [css.founderBadgeTooltipVisible]: isTooltipVisible,
      })}
      tabIndex={0}
      aria-describedby={tooltipId}
      onMouseEnter={() => setIsTooltipVisible(true)}
      onMouseLeave={() => setIsTooltipVisible(false)}
      onFocus={() => setIsTooltipVisible(true)}
      onBlur={() => setIsTooltipVisible(false)}
      onClick={() => setIsTooltipVisible(true)}
      onTouchStart={() => setIsTooltipVisible(true)}
    >
      <img
        className={css.founderBadgeIcon}
        src="/static/icons/resuelvepr-founder-48.png"
        alt="ResuelvePR"
      />
      <span>Fundador</span>
      <span id={tooltipId} role="tooltip" className={css.founderBadgeTooltip}>
        Proveedor Fundador: parte de la primera comunidad de proveedores de ResuelvePR.
      </span>
    </div>
  ) : null;
};

const LANGUAGE_BADGE_LABELS = {
  spanish: 'ESP',
  english: 'ENG',
};

export const getLanguageBadgeLabel = languages => {
  if (!Array.isArray(languages) || languages.length === 0) {
    return null;
  }

  const hasOnlyRecognizedLanguages = languages.every(language => LANGUAGE_BADGE_LABELS[language]);
  if (!hasOnlyRecognizedLanguages) {
    return null;
  }

  const labels = ['spanish', 'english']
    .filter(language => languages.includes(language))
    .map(language => LANGUAGE_BADGE_LABELS[language]);

  return labels.length > 0 ? labels.join(' \u2022 ') : null;
};

export const LanguageBadge = props => {
  const { languages, className } = props;
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipId = useId();
  const label = getLanguageBadgeLabel(languages);

  return label ? (
    <div
      className={classNames(css.founderBadge, css.languageBadge, className, {
        [css.languageBadgeTooltipVisible]: isTooltipVisible,
      })}
      tabIndex={0}
      aria-describedby={tooltipId}
      onMouseEnter={() => setIsTooltipVisible(true)}
      onMouseLeave={() => setIsTooltipVisible(false)}
      onFocus={() => setIsTooltipVisible(true)}
      onBlur={() => setIsTooltipVisible(false)}
      onClick={() => setIsTooltipVisible(true)}
      onTouchStart={() => setIsTooltipVisible(true)}
    >
      <svg
        className={css.languageBadgeIcon}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M5 5.5h14v10H9.5L5 19v-3.5H5z" />
        <path d="M8.5 9.5h7M8.5 12.5h4.5" />
      </svg>
      <span>{label}</span>
      <span id={tooltipId} role="tooltip" className={css.languageBadgeTooltip}>
        Idioma/s en que se comunica
      </span>
    </div>
  ) : null;
};

export const ProfileReviewSummary = props => {
  const intl = useIntl();
  const { reviews = [], targetId } = props;
  const reviewCount = reviews.length;
  const ratingTotal = reviews.reduce((total, review) => total + review.attributes.rating, 0);
  const averageRating = reviewCount > 0 ? ratingTotal / reviewCount : null;
  const averageRatingText = averageRating?.toFixed(1);
  const ariaLabel = reviewCount
    ? intl.formatMessage(
        { id: 'ProfilePage.reviewSummaryAriaLabel' },
        { average: averageRatingText, count: reviewCount }
      )
    : intl.formatMessage({ id: 'ProfilePage.reviewSummaryNoReviewsAriaLabel' });

  const scrollToReviews = event => {
    const target = typeof document !== 'undefined' ? document.getElementById(targetId) : null;

    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    }
  };

  return (
    <a
      className={css.providerReviewSummary}
      href={`#${targetId}`}
      aria-label={ariaLabel}
      onClick={scrollToReviews}
    >
      {reviewCount === 0 ? (
        <span className={css.noProviderReviews}>
          <FormattedMessage id="ProfilePage.providerReviewSummaryNoReviews" />
        </span>
      ) : (
        <div className={css.providerReviewSummaryRow}>
          <ReviewRating
            className={css.providerReviewStars}
            reviewStarClassName={css.providerReviewStar}
            rating={Math.round(averageRating)}
          />
          <span className={css.providerReviewAverage}>{averageRatingText}</span>
          <span className={css.providerReviewSeparator} aria-hidden="true">
            ·
          </span>
          <span className={css.providerReviewCount}>
            <FormattedMessage
              id="ProfilePage.providerReviewSummaryCount"
              values={{ count: reviewCount }}
            />
          </span>
        </div>
      )}
    </a>
  );
};

export const AsideContent = props => {
  const {
    user,
    displayName,
    providerBadge,
    providerLanguages,
    providerReviews,
    reviewSummaryTargetId,
    showProviderReviewSummary,
    showLinkToProfileSettingsPage,
    showReportProfile,
    onManageDisableScrolling,
  } = props;

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [reportInProgress, setReportInProgress] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSent, setReportSent] = useState(false);
  const hasProfileBadges = providerBadge === 'founder' || getLanguageBadgeLabel(providerLanguages) != null;

  const resetReport = () => {
    setReason('');
    setExplanation('');
    setReportError(null);
    setReportSent(false);
  };

  const openReportModal = () => {
    resetReport();
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    if (!reportInProgress) {
      setReportModalOpen(false);
      resetReport();
    }
  };

  const handleReportSubmit = event => {
    event.preventDefault();

    const cleanExplanation = explanation.trim();

    if (!reason || !cleanExplanation) {
      setReportError('Selecciona una razón y escribe una explicación.');
      return;
    }

    setReportInProgress(true);
    setReportError(null);

    reportProfile({
      reportedUserId: user?.id?.uuid,
      reportedDisplayName: displayName || '',
      profileUrl: typeof window !== 'undefined' ? window.location.href : '',
      reason,
      explanation: cleanExplanation,
    })
      .then(() => {
        setReportSent(true);
      })
      .catch(() => {
        setReportError('No se pudo enviar el reporte. Intenta nuevamente.');
      })
      .finally(() => {
        setReportInProgress(false);
      });
  };

  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />

      <div className={css.mobileProfileHeading}>
        <div
          className={classNames(css.mobileBadges, {
            [css.mobileBadgesVisible]: hasProfileBadges,
          })}
        >
          <FounderBadge providerBadge={providerBadge} />
          <LanguageBadge languages={providerLanguages} />
        </div>
        <H2 as="h1" className={css.mobileHeading}>
          {displayName ? (
            <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
          ) : null}
        </H2>
      </div>

      {showProviderReviewSummary ? (
        <ProfileReviewSummary reviews={providerReviews} targetId={reviewSummaryTargetId} />
      ) : null}

      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}

      {showReportProfile ? (
        <>
          <button
            type="button"
            className={css.reportProfileButton}
            onClick={openReportModal}
          >
            Reportar perfil
          </button>

          <Modal
            id="ProfilePage.reportProfile"
            isOpen={reportModalOpen}
            onClose={closeReportModal}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
            focusElementId="profile-report-reason"
          >
            <div className={css.reportModalContent}>
              {reportSent ? (
                <div className={css.reportSuccess}>
                  <H2 as="h2" className={css.reportTitle}>
                    Reporte enviado
                  </H2>

                  <p>
                    Recibimos tu reporte. ResuelvePR evaluará la información enviada.
                    Si deseas añadir detalles o evidencia, escríbenos a{' '}
                    <a href="mailto:atencion@resuelve-pr.com">
                      atencion@resuelve-pr.com
                    </a>.
                  </p>

                  <button
                    type="button"
                    className={css.reportPrimaryButton}
                    onClick={closeReportModal}
                  >
                    Cerrar
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReportSubmit}>
                  <H2 as="h2" className={css.reportTitle}>
                    Reportar perfil
                  </H2>

                  <p className={css.reportIntro}>
                    Selecciona la razón y explica claramente lo ocurrido. Los
                    reportes vacíos no serán enviados.
                  </p>

                  <label className={css.reportLabel} htmlFor="profile-report-reason">
                    Razón
                  </label>

                  <select
                    id="profile-report-reason"
                    className={css.reportSelect}
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    required
                  >
                    <option value="">Selecciona una razón</option>
                    <option value="fake-profile">Perfil falso</option>
                    <option value="inappropriate-content">
                      Foto o nombre inapropiado
                    </option>
                    <option value="scam-spam">Estafa o spam</option>
                    <option value="harassment">Acoso</option>
                    <option value="off-platform">
                      Intento de mover el trabajo o pago fuera de ResuelvePR
                    </option>
                    <option value="misleading-info">
                      Experiencia o información engañosa
                    </option>
                    <option value="other">Otro</option>
                  </select>

                  <label
                    className={css.reportLabel}
                    htmlFor="profile-report-explanation"
                  >
                    Explicación
                  </label>

                  <textarea
                    id="profile-report-explanation"
                    className={css.reportTextarea}
                    value={explanation}
                    onChange={event => setExplanation(event.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="Describe lo ocurrido y cualquier información que ayude a revisar el reporte."
                    required
                  />

                  <div className={css.reportCharacterCount}>
                    {explanation.length}/2000
                  </div>

                  {reportError ? (
                    <p className={css.reportError} role="alert">
                      {reportError}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    className={css.reportPrimaryButton}
                    disabled={reportInProgress || !reason || !explanation.trim()}
                  >
                    {reportInProgress ? 'Enviando…' : 'Enviar reporte'}
                  </button>
                </form>
              )}
            </div>
          </Modal>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <section id="provider-reviews" tabIndex="-1" className={css.reviewSection}>
        <H4 as="h2" className={css.mobileReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </H4>
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        <Reviews reviews={reviewsOfProvider} />
      </section>
      <section id="customer-reviews" tabIndex="-1" className={css.reviewSection}>
        <H4 as="h2" className={css.mobileReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </H4>
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        <Reviews reviews={reviewsOfCustomer} />
      </section>
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div
      id={isReviewTypeProviderSelected ? 'provider-reviews' : 'customer-reviews'}
      tabIndex="-1"
      className={css.desktopReviews}
    >
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;
  const userFieldsWithoutProfileBadges = userFieldConfig?.filter(
    fieldConfig => !['providerBadge', 'idiomas'].includes(fieldConfig.key)
  );

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldsWithoutProfileBadges,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldsWithoutProfileBadges,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    isOwnProfile,
    ownClientListings = [],
    listingClassifications = {},
    archiveDraftInProgressId,
    archiveDraftError,
    onArchiveDraft,
    onManageDisableScrolling,
  } = props;

  const providerBadge = userTypeRoles?.provider ? metadata?.providerBadge : null;
  const providerLanguages = publicData?.idiomas;
  const hasProfileBadges = providerBadge === 'founder' || getLanguageBadgeLabel(providerLanguages) != null;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <div
        className={classNames(css.desktopBadges, {
          [css.desktopBadgesVisible]: hasProfileBadges,
        })}
      >
        <FounderBadge providerBadge={providerBadge} />
        <LanguageBadge languages={providerLanguages} />
      </div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {isOwnProfile ? (
        <OwnClientListings
          listings={ownClientListings}
          classifications={listingClassifications}
          intl={intl}
          ownerId={props.ownerId}
          archiveDraftInProgressId={archiveDraftInProgressId}
          archiveDraftError={archiveDraftError}
          onArchiveDraft={onArchiveDraft}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const onArchiveDraft = useCallback(
    (listingId, ownerId) => dispatch(archiveOwnDraft(listingId, ownerId)),
    [dispatch]
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);
  const providerReviews = (rest.reviews || []).filter(
    review => review.attributes.type === REVIEW_TYPE_OF_PROVIDER
  );
  const customerReviews = (rest.reviews || []).filter(
    review => review.attributes.type === REVIEW_TYPE_OF_CUSTOMER
  );
  const reviewSummary = userTypeRoles?.provider
    ? { reviews: providerReviews, targetId: 'provider-reviews' }
    : userTypeRoles?.customer
    ? { reviews: customerReviews, targetId: 'customer-reviews' }
    : null;

  const showReportProfile =
    mounted &&
    !!currentUser?.id &&
    !isCurrentUser &&
    userTypeRoles?.provider === true;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            showReportProfile={showReportProfile}
            onManageDisableScrolling={onManageDisableScrolling}
            displayName={displayName}
            providerBadge={userTypeRoles?.provider ? metadata?.providerBadge : null}
            providerLanguages={publicData?.idiomas}
            providerReviews={reviewSummary?.reviews}
            reviewSummaryTargetId={reviewSummary?.targetId}
            showProviderReviewSummary={reviewSummary != null}
          />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          isOwnProfile={isCurrentUser}
          ownerId={currentUser?.id}
          onArchiveDraft={onArchiveDraft}
          onManageDisableScrolling={onManageDisableScrolling}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    ownClientListingRefs = [],
    listingClassifications = {},
    archiveDraftInProgressId,
    archiveDraftError,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    ownClientListings: getMarketplaceEntities(state, ownClientListingRefs),
    listingClassifications,
    archiveDraftInProgressId,
    archiveDraftError,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
